'use client'

import { type FormEvent, useState } from 'react'
import { Coins, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCreateCreditTopUpCheckout } from '@/hooks/use-billing'
import {
  CREDIT_PACK_MAX_CREDITS,
  CREDIT_PACK_MIN_CREDITS,
  CREDIT_PACK_STEP_CREDITS,
  creditPackPriceUsd,
  isValidCreditPackAmount,
} from '@/lib/credit-pack'

interface CreditTopUpDialogProps {
  open: boolean
  returnUrls?: { successUrl: string; cancelUrl: string }
  onOpenChange: (open: boolean) => void
}

export function CreditTopUpDialog({
  open,
  onOpenChange,
  returnUrls,
}: CreditTopUpDialogProps) {
  const t = useTranslations('SettingsPages.billing.creditTopUp')
  const [credits, setCredits] = useState(10_000)
  const [error, setError] = useState<string | null>(null)
  const { createCheckout, isMutating } = useCreateCreditTopUpCheckout()
  // No admin gate: `POST /api/billing/credits/checkout` takes RequireOrgMember,
  // so any member may top up. A top-up only ever adds credits, and the member
  // who exhausted them is the one who needs more.
  const valid = isValidCreditPackAmount(credits)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!valid) {
      setError(t('invalidAmount'))
      return
    }
    setError(null)
    try {
      await createCheckout(credits, returnUrls)
    } catch (checkoutError) {
      console.error('[credit-top-up-dialog]', checkoutError)
      setError(t('checkoutError'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[440px] max-w-[calc(100vw-2rem)]">
        <form onSubmit={handleSubmit} className="space-y-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="size-4" />
              {t('title')}
            </DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="credit-top-up-amount">{t('amountLabel')}</Label>
            <Input
              id="credit-top-up-amount"
              type="number"
              min={CREDIT_PACK_MIN_CREDITS}
              max={CREDIT_PACK_MAX_CREDITS}
              step={CREDIT_PACK_STEP_CREDITS}
              value={credits}
              onChange={event => setCredits(Number(event.target.value))}
              disabled={isMutating}
              aria-invalid={error ? true : undefined}
            />
            <p className="text-muted-foreground text-xs">{t('rangeHint')}</p>
          </div>

          <div className="bg-muted/40 flex items-center justify-between rounded-lg border p-4">
            <span className="text-sm">{t('total')}</span>
            <span className="font-mono text-lg font-semibold tracking-tight">
              ${valid ? creditPackPriceUsd(credits).toLocaleString() : '—'}
            </span>
          </div>

          {error ? <p className="text-destructive text-sm">{error}</p> : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isMutating}
            >
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!valid || isMutating}>
              {isMutating ? <Loader2 className="size-4 animate-spin" /> : null}
              {isMutating ? t('redirecting') : t('checkout')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
